import dayjs from "dayjs";
import { ArrowRight, Calendar, Bell, BellOff, Trash2, Pencil } from "lucide-react";
import { cn } from "@/utils/css";
import { RadioCircle } from "@/components/common/inputs";
import { formatLocation } from "@/utils/text";

const Pill = ({ children }) => (
    <span className="flex items-center gap-1 h-[20px] px-2 py-1 rounded-[44px] bg-cm-p-surface-container text-[12px] font-medium tracking-[-0.12px] text-cm-p-text-primary whitespace-nowrap">
        {children}
    </span>
);

export const FilterCard = ({
    filter,
    selected,
    onToggleSelect,
    onDelete,
    onEdit,
    unreadCount = 0,
    notificationsDisabled = false,
    onToggleNotifications,
    loadCount = 0,
}) => {
    const departure = filter.departure;
    const dropOff = filter.dropOff;
    // Extract date part (YYYY-MM-DD) to avoid timezone conversion issues
    const startDate = filter.availableDateStart ? dayjs(filter.availableDateStart.slice(0, 10)) : null;
    const endDate = filter.availableDateEnd ? dayjs(filter.availableDateEnd.slice(0, 10)) : null;
    const minOffer = filter.minOffer;

    const departureLabel = departure ? formatLocation(departure.city, departure.state) : "Unknown";
    const dropOffLabel = dropOff ? formatLocation(dropOff.city, dropOff.state) : "Any destination";

    const dateLabel = (() => {
        if (!startDate) return null;
        const startFormatted = startDate.format("MMM D");
        if (!endDate || startDate.isSame(endDate, "day")) return startFormatted;
        return `${startFormatted} - ${endDate.format("MMM D")}`;
    })();
    const offerLabel = minOffer != null ? `$ ${minOffer.toLocaleString()} +` : null;

    return (
        <div
            className="flex items-center gap-4 h-[32px] rounded-[6px] cursor-pointer"
            onClick={onToggleSelect}
        >
            {/* Radio circle */}
            <div className="flex items-center justify-center w-6 h-6 shrink-0">
                <RadioCircle selected={selected} />
            </div>

            {/* Route + pills */}
            <div className="flex items-center gap-3 flex-1 min-w-0">
                <div className="flex items-center gap-3 w-[300px] shrink-0">
                    {/* Origin */}
                    <div className="flex items-center gap-1">
                        <div className="flex items-center justify-center w-3 h-3">
                            <div className="w-2 h-2 rounded bg-cm-p-on-container-green" />
                        </div>
                        <span className="text-[14px] font-medium text-cm-p-text-primary whitespace-nowrap">
                            {departureLabel}
                        </span>
                    </div>

                    <ArrowRight className="w-4 h-4 text-cm-p-text-secondary shrink-0" />

                    {/* Destination */}
                    <div className="flex items-center gap-1">
                        <GridIcon className="w-3 h-3 text-cm-p-on-container-green" />
                        <span
                            className={cn(
                                "text-[14px] whitespace-nowrap",
                                dropOff ? "font-medium text-cm-p-text-primary" : "text-cm-p-text-secondary",
                            )}
                        >
                            {dropOffLabel}
                        </span>
                    </div>
                </div>

                {/* Pills */}
                <div className="flex items-center gap-2">
                    <Pill>
                        {loadCount} loads
                    </Pill>
                    {dateLabel && (
                        <Pill>
                            <Calendar className="w-3 h-3 text-cm-p-text-primary" />
                            {dateLabel}
                        </Pill>
                    )}
                    {offerLabel && <Pill>{offerLabel}</Pill>}
                    <Pill>All brokers</Pill>
                </div>
            </div>

            {/* Unread badge + actions */}
            <div className="flex items-center gap-2 shrink-0">
                {unreadCount > 0 && !notificationsDisabled && (
                    <div className="flex items-center justify-center h-6 min-w-6 px-2 rounded-full bg-cm-p-accent-400">
                        <span className="text-[15px] font-semibold text-cm-p-neutral-dark-500 tracking-[-0.15px]">
                            {unreadCount}
                        </span>
                    </div>
                )}

                <button
                    className="flex items-center justify-center w-8 h-8 text-cm-p-text-primary hover:text-cm-p-on-container-green"
                    onClick={(e) => {
                        e.stopPropagation();
                        onEdit?.(filter);
                    }}
                >
                    <Pencil className="w-4 h-4" />
                </button>
                <button
                    className={cn(
                        "flex items-center justify-center w-8 h-8",
                        notificationsDisabled
                            ? "text-cm-p-text-secondary"
                            : "text-cm-p-text-primary hover:text-cm-p-on-container-green",
                    )}
                    onClick={(e) => {
                        e.stopPropagation();
                        onToggleNotifications?.();
                    }}
                    title={notificationsDisabled ? "Enable notifications" : "Disable notifications"}
                >
                    {notificationsDisabled ? <BellOff className="w-4 h-4" /> : <Bell className="w-4 h-4" />}
                </button>
                <button
                    className="flex items-center justify-center w-8 h-8 text-cm-p-on-container-red"
                    onClick={(e) => {
                        e.stopPropagation();
                        onDelete?.(filter);
                    }}
                >
                    <Trash2 className="w-4 h-4" />
                </button>
            </div>
        </div>
    );
};

const GridIcon = ({ className }) => (
    <svg className={className} width="12" height="12" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg">
        <rect x="4.01758" y="3.73828" width="3.96571" height="3.96571" fill="currentColor" />
        <rect x="1.5" y="1.5" width="3.00037" height="3.00037" fill="currentColor" />
        <rect x="7.5" y="7.5" width="3.00037" height="3.00037" fill="currentColor" />
        <rect x="7.48047" y="1.5" width="3.00037" height="3.00037" fill="currentColor" />
        <rect x="1.51953" y="7.48828" width="3.00037" height="3.00037" fill="currentColor" />
    </svg>
);
